import { useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { useModalContext } from '@/components/ui/modal-provider';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { captureFeatureBlocked } from '@/lib/posthog';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import AIGeneratorCard from './AIGeneratorCard';
import AnamnesisUploadModal from './AnamnesisUploadModal';
import { SECTIONS } from './constants';
import { WizardStepProps } from './types';
import type { SourceActionsMap } from './AIDataSources';

export default function HistoriaClinicaStep({ data, onUpdate }: WizardStepProps) {
    const {
        silentSave,
        sectionRequirements,
        setIsGenerating,
        setGeneratingSectionId,
        beginGeneration,
        endGeneration,
    } = useReportWizard();
    const { openModal } = useModalContext();
    const anamnesisAccess = useFeatureEntitlement('anamnesis.process');

    // See DemandaStep: reading the report through a ref keeps the handlers
    // stable across the re-render every keystroke in the document causes.
    const latest = useRef({ data, onUpdate });
    latest.current = { data, onUpdate };

    const handleOpenAnamnesisModal = useCallback(() => {
        if (anamnesisAccess.shouldBlock) {
            captureFeatureBlocked('anamnesis_modal', anamnesisAccess.decision);
            toast.warning(getEntitlementBlockedMessage(anamnesisAccess.decision));
            return;
        }

        openModal({
            // No title here on purpose: the modal renders its own
            // `AppDialogLayout` so the paste view can put a back control in the
            // header row, next to the title and the close.
            content: (
                <AnamnesisUploadModal
                    data={latest.current.data}
                    onUpdate={latest.current.onUpdate}
                />
            ),
            size: 'lg',
        });
    }, [anamnesisAccess.shouldBlock, anamnesisAccess.decision, openModal]);

    const sourceActions = useMemo<SourceActionsMap>(
        () => ({
            anamnesis: {
                // Three labels, one modal. Which one shows depends on the state
                // the panel is in, and each names what you are actually about
                // to do there: put a document in, change the one that is in, or
                // retry the preparation that broke.
                label: 'Adicionar anamnese',
                manageLabel: 'Gerenciar anamnese',
                // This is the only source that can fail — `failed` comes solely
                // from `clinicalHistoryStatus`, which only resolves in this
                // step. The route out is the same modal, whose own copy already
                // says "Tente reenviar a anamnese".
                failedLabel: 'Reenviar anamnese',
                disabled: anamnesisAccess.shouldBlock,
                disabledTooltip: getEntitlementBlockedMessage(anamnesisAccess.decision),
                unavailablePlan:
                    anamnesisAccess.isUnavailableInPlan && anamnesisAccess.availableFromPlan
                        ? anamnesisAccess.availableFromPlan
                        : null,
                onClick: handleOpenAnamnesisModal,
            },
        }),
        [
            anamnesisAccess.shouldBlock,
            anamnesisAccess.decision,
            anamnesisAccess.isUnavailableInPlan,
            anamnesisAccess.availableFromPlan,
            handleOpenAnamnesisModal,
        ],
    );

    const handleAIGenerate = async () => {
        if (!data.id) return;

        setIsGenerating(true);
        setGeneratingSectionId(SECTIONS.CLINICAL_HISTORY);
        const generation = beginGeneration();
        await silentSave();
        try {
            const response = await aiApi.generate(
                data.id,
                SECTIONS.CLINICAL_HISTORY,
                undefined,
                'create',
                undefined,
                generation.options,
            );
            onUpdate({
                clinical_history: {
                    ...data.clinical_history,
                    content: response.content,
                },
            });
            toast.success('Rascunho da história clínica gerado. Revise no laudo ao lado.');
        } catch (error: unknown) {
            // The user stopped it. The section is untouched and no credit was
            // spent, and the button they pressed is answer enough.
            if (isAiGenerationCancelled(error)) return;

            toast.error((error as Error).message || 'Erro ao gerar conteúdo.');
        } finally {
            setIsGenerating(false);
            setGeneratingSectionId(null);
            endGeneration();
        }
    };

    return (
        <div className="flex h-full flex-col">
            <AIGeneratorCard
                onGenerate={handleAIGenerate}
                sourceStepId={SECTIONS.CLINICAL_HISTORY}
                reportData={data}
                sourceActions={sourceActions}
                backendRequirements={sectionRequirements}
            />
        </div>
    );
}
